import {ActionTree} from 'vuex'
import {AxiosResponse, AxiosError} from "axios";
import moment from 'moment';
import ILocale from "@/modules/PanelSettings/ts/ILocale";
// @ts-ignore
import Api from "@/api.js";

export const actions: ActionTree<any, any> = {

    getSettings: ({state, getters, commit, dispatch}, force) => {

        if (force) commit('bustSettingsCache');

        commit('setSettingsLoading', true);

        return new Promise((resolve, reject) => {

            // Pull from store if loaded and cache hasn't expired
            if (state.settingsExpire && moment().isBefore(state.settingsExpire)) {
                commit('setSettingsLoading', false);
                resolve(state.settings);
                return;
            }

            Api.get('/panel')

                .then((response: AxiosResponse) => {
                    commit('saveSettings', response.data.settings);
                    commit('setSettingsLoading', false);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    commit('setSettingsLoading', false);
                    reject(error);
                });
        });
    },

    saveSettings: ({state, getters, commit, dispatch}, form) => {

        commit('setSettingsSaving', true);

        return new Promise((resolve, reject) => {

            Api.put('/panel', form.data)

                .then((response: AxiosResponse) => {
                    commit('saveSettings', response.data.settings);
                    commit('setSettingsSaving', false);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    commit('setSettingsSaving', false);
                    reject(error);
                });
        });
    },

    enableLocale: ({state, getters, commit, dispatch}, locale) => {

        commit('enableLocale', locale);

        return new Promise((resolve, reject) => {
            dispatch('updateLocales')
                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error) => {
                    commit('disableLocale', locale);
                    reject(error);
                });
        });
    },

    disableLocale: ({state, getters, commit, dispatch}, locale) => {

        commit('disableLocale', locale);

        return new Promise((resolve, reject) => {
            dispatch('updateLocales')
                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error) => {
                    commit('enableLocale', locale);
                    reject(error);
                });
        });
    },

    updateLocales: ({state, commit}) => {

        return new Promise((resolve, reject) => {

            const localeIds = state.settings.locales.map((locale: ILocale) => locale.id);

            Api.put('/panel/locales', {locales: localeIds})

                .then((response: AxiosResponse) => {
                    commit('updateLocaleDataVariable', null, {root: true})
                    resolve(response);

                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    getApiClients: ({commit}) => {

        return new Promise((resolve, reject) => {

            Api.get('/panel/api-clients')
                .then((response: AxiosResponse) => {
                    commit('setApiClients', response.data.clients);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                })
        });
    },

    createApiClient: ({state, commit}, payload) => {

        return new Promise((resolve, reject) => {

            Api.put('/panel/api-clients', payload)
                .then((response: AxiosResponse) => {
                    commit('addApiClient', response.data.client);
                    resolve(response)
                })
                .catch((error: AxiosError) => {
                    reject(error);
                })
        });
    },

    deleteApiClient: ({state, commit}, id: Number) => {

        const apiClients = [...state.apiClients];

        commit('deleteApiClient', id);

        return new Promise((resolve, reject) => {

            Api.delete('/panel/api-clients/' + id)
                .then((response: AxiosResponse) => {
                    resolve(response)
                })
                .catch((error: AxiosError) => {
                    commit('setApiClients', apiClients);
                    reject(error);
                })
        });
    },

    getIntegrations: () => {

        return new Promise((resolve, reject) => {

            Api.get('/panel/integrations')
                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                })
        });
    },

    saveIntegrationSettings: ({state, commit}, payload) => {

        return new Promise((resolve, reject) => {

            Api.post('/panel/integrations', payload)
                .then((response: AxiosResponse) => {
                    commit('saveIntegrations', response.data.integrations);
                    resolve(response)
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    reject(error);
                })
        });
    },

    getBlacklistedPanelists: ({state, commit}, payload) => {
        return new Promise((resolve, reject) => {

            const queryString = new URLSearchParams({
                page: payload.page,
                itemsPerPage: payload.itemsPerPage,
                sortBy: payload.sortBy[0],
                sortDesc: payload.sortDesc[0] === true ? '1' : '0',
            });

            if (payload.search) {
                queryString.append('search', payload.search);
            }

            Api.get(`/panel/blacklisted?${queryString.toString()}`)
                .then((response: AxiosResponse) => {
                    resolve(response.data)
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    reject(error);
                })
        });
    },

    insertBlacklistedPanelists: ({state, commit}, payload) => {
        return new Promise((resolve, reject) => {
            Api.post('/panel/blacklisted', {list: payload})
                .then((response: AxiosResponse) => {
                    resolve(response.data);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    deleteBlacklistedPanelists: ({}, payload) => {
        return new Promise((resolve, reject) => {
            Api.delete('/panel/blacklisted', {data: {ids: payload}})
                .then((response: AxiosResponse) => {
                    resolve(response)
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    reject(error);
                })
        });
    },

    getLocales: ({state, getters, commit, dispatch, rootState}, force) => {

        if (force) commit('resetLocalesCache');

        commit('setLocalesLoading', true);

        return new Promise((resolve, reject) => {

            if (state.localesExpire && moment().isBefore(state.localesExpire)) {
                commit('setLocalesLoading', false);
                resolve(state.locales);
                return;
            }

            Api.get('/locales')

                .then(({data}: AxiosResponse) => {

                    commit('setLocales', data.locales);
                    commit('setLocalesLoading', false);
                    resolve(null);
                })
                .catch((error: AxiosError) => {
                    commit('setLocalesLoading', false);
                    reject(error);
                });
        });
    },

    getServiceCreditTransactions: ({state, getters, commit, dispatch, rootState}, cursor) => {

        return new Promise((resolve, reject) => {

            let url = '/panel/service-credits';

            if (cursor) {
                url += '?cursor=' + cursor;
            }

            Api.get(url)

                .then((response: AxiosResponse) => {
                    commit('setServiceCreditPrices', response.data.prices);
                    commit('setServiceCreditTransactions', response.data.data);
                    commit('setServiceCreditTableNextCursor', response.data.next_cursor);
                    commit('setServiceCreditTablePrevCursor', response.data.prev_cursor);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    updateServiceCreditBalanceAlarm: ({state, getters, commit, dispatch, rootState}, serviceCreditBalanceAlarm) => {

        return new Promise((resolve, reject) => {

            let url = '/panel/service-credits/alarm';

            const data = {
                service_credit_balance_alarm: serviceCreditBalanceAlarm
            };

            Api.put(url, data)

                .then((response: AxiosResponse) => {
                    commit('setServiceCreditBalanceAlarm', serviceCreditBalanceAlarm);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    sendServiceCreditInvoice: ({state, getters, commit, dispatch, rootState}, data) => {

        return new Promise((resolve, reject) => {

            Api.post('/panel/service-credits/invoice', data)

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    }
}
